import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, PiggyBank, Calculator, TrendingUp, Home, Building2, CreditCard, Check, UserPlus } from 'lucide-react';
import { solutions, whyUs } from '../mock';
import WeatherWidget from '../components/WeatherWidget';
import GoogleReviewsPremium from '../components/GoogleReviewsPremium';
import SEO, { SEOPages } from '../components/SEO';
import axios from 'axios';

import API_BASE_URL from '../config/api';
const API = API_BASE_URL;

const Accueil = () => {
  const [quickContactData, setQuickContactData] = useState({
    nom: '',
    prenom: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const iconComponents = {
    Shield,
    PiggyBank,
    Calculator,
    TrendingUp,
    Home,
    Building2,
    CreditCard
  };

  const handleQuickContact = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    
    try {
      const response = await axios.post(`${API}/contact`, {
        ...quickContactData,
        theme: 'Demande générale',
        message: 'Demande via formulaire rapide page d\'accueil'
      });
      
      if (response.data.success) {
        setSubmitMessage('✅ Demande envoyée ! Nous vous contacterons rapidement.');
        setQuickContactData({ nom: '', prenom: '', email: '' });
      } else {
        setSubmitMessage('❌ Erreur lors de l\'envoi. Veuillez réessayer.');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setSubmitMessage('❌ Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuickContactChange = (field, value) => {
    setQuickContactData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="pt-20">
      <SEO {...SEOPages.home} />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Background avec image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          role="img"
          aria-label="Bureau moderne de conseil financier avec vue sur Neuchâtel"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1523211737006-e54a3c7299ab)',
            backgroundBlendMode: 'overlay'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40"></div>
        </div>
        
        <div className="container relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Contenu principal */}
            <div className="space-y-8">
              <div className="space-y-6">
                <h1 className="text-5xl lg:text-6xl font-bold heading-display text-white leading-tight">
                  Des décisions financières 
                  <span className="text-blue-300"> claires</span>, simplement.
                </h1>
                
                <div className="flex flex-wrap gap-4 text-lg">
                  <span className="px-4 py-2 glass rounded-full text-blue-200 font-medium">
                    • Indépendance
                  </span>
                  <span className="px-4 py-2 glass rounded-full text-blue-200 font-medium">
                    • Transparence
                  </span>
                  <span className="px-4 py-2 glass rounded-full text-blue-200 font-medium">
                    • Proximité
                  </span>
                </div>
                
                <p className="text-xl text-gray-300 leading-relaxed">
                  Conseil financier indépendant et transparent pour votre avenir. 
                  Nous vous accompagnons dans toutes vos décisions financières importantes.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row flex-wrap gap-4">
                <Link to="/contact" className="btn-glass btn-primary text-lg px-8 py-4 flex items-center justify-center" data-testid="hero-cta-contact">
                  Démarrer mon analyse
                  <ArrowRight className="ml-2" size={20} />
                </Link>
                <Link to="/a-propos" className="btn-glass text-lg px-8 py-4 flex items-center justify-center" data-testid="hero-cta-team">
                  Découvrir notre équipe
                </Link>
                <Link to="/recrutement" className="btn-glass text-lg px-8 py-4 flex items-center justify-center group hover:border-blue-400/50" data-testid="hero-cta-recruitment">
                  <UserPlus className="mr-2 group-hover:scale-110 transition-transform" size={20} />
                  Rejoindre notre équipe
                </Link>
              </div>
            </div>

            {/* Widget météo - positionné en haut sur mobile */}
            <div data-testid="weather-widget" className="lg:self-start">
              <WeatherWidget />
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi nous */}
      <section className="py-20" data-testid="why-us-section">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Pourquoi choisir ATOM Group SA ?
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Notre approche indépendante et transparente fait la différence
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyUs.map((item, index) => (
              <div key={item.id} className="glass p-8 text-center group" data-testid={`why-us-card-${item.id}`}>
                <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <Check size={32} className="text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">{item.title}</h3>
                <p className="text-gray-300">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-20" data-testid="solutions-section">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Nos solutions
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Des services complets pour tous vos besoins financiers
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution) => {
              const IconComponent = iconComponents[solution.icon];
              return (
                <Link 
                  key={solution.id}
                  to={solution.path}
                  className="glass p-8 group hover:bg-blue-500/10 transition-all duration-300"
                  data-testid={`solution-card-${solution.id}`}
                >
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                      <IconComponent size={24} className="text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-white">{solution.title}</h3>
                  </div>
                  <p className="text-gray-300 mb-6">{solution.description}</p>
                  <div className="flex items-center text-blue-300 group-hover:text-blue-200">
                    <span className="text-sm font-medium">En savoir plus</span>
                    <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Avis Google Premium */}
      <GoogleReviewsPremium />

      {/* CTA Final */}
      <section className="py-20" data-testid="final-cta-section">
        <div className="container">
          <div className="glass p-12 rounded-3xl text-center">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Prêt à optimiser vos finances ?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Bénéficiez d'une première analyse gratuite et découvrez comment nous pouvons vous aider à atteindre vos objectifs financiers.
            </p>
            <Link to="/contact" className="btn-glass btn-primary text-lg px-12 py-4 inline-flex items-center" data-testid="final-cta-contact">
              Réserver un rendez-vous
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Accueil;